import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { BrushfireMarkIcon } from '@/shared/components/AppShell/BrushfireMarkIcon'
import {
  PROFILE_MENU_ICON_PX,
  ProfileMenuIconLogout,
  ProfileMenuIconSettings,
} from '@/shared/components/AppShell/ProfileMenuIcons'
import type { NavSection } from '@/shared/components/AppShell/nav-section'

const NAV_CYAN = '#0fd4fc'
/** New Color Images favicon orange (`#FF9933`). */
const MARK_TILE_BG = '#ff9933'
const AVATAR_BG = '#008cd0'
const MENU_HOVER_BG = '#dbeafe'

export type AppShellNavItem = Readonly<{
  label: string
  path: string
  section: NavSection
  icon: ReactNode
}>

export type AppShellSidebarProps = Readonly<{
  visibleItems: readonly AppShellNavItem[]
  activeSection: NavSection | null
  onNavActivate: (item: AppShellNavItem) => void
  displayName: string
  initials: string
  roleLabel: string
  profileMenuAnchor: HTMLElement | null
  profileMenuOpen: boolean
  onProfileMenuOpen: (event: React.SyntheticEvent<HTMLElement>) => void
  onProfileMenuClose: () => void
  onAccountSettings: () => void
  onLogout: () => void
  logoutPending: boolean
  onLogoClick: () => void
}>

/** Sidebar nav + profile block (Figma `4107:1270`). */
export function AppShellSidebar({
  visibleItems,
  activeSection,
  onNavActivate,
  displayName,
  initials,
  roleLabel,
  profileMenuAnchor,
  profileMenuOpen,
  onProfileMenuOpen,
  onProfileMenuClose,
  onAccountSettings,
  onLogout,
  logoutPending,
  onLogoClick,
}: AppShellSidebarProps) {
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={onLogoClick}
        aria-label="Go to dashboard"
        data-testid="sensemaker-nav-logo"
        sx={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          width: '100%',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '85px',
          px: '24px',
          pt: '24px',
          pb: 0,
          flexShrink: 0,
          boxSizing: 'border-box',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          '&:focus-visible': { outline: '2px solid #0fd4fc', outlineOffset: -2 },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            bgcolor: MARK_TILE_BG,
            boxSizing: 'border-box',
            p: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          <BrushfireMarkIcon />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#ffffff',
              whiteSpace: 'nowrap',
            }}
          >
            BRUSHFIRE
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: '#94a3b8',
              whiteSpace: 'nowrap',
            }}
          >
            Sense Maker
          </Typography>
        </Box>
      </Box>

      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{
          flex: 1,
          px: '16px',
          pt: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {visibleItems.map((item) => {
          const isActive = item.section === activeSection
          return (
            <Box
              key={item.path}
              role="button"
              tabIndex={0}
              data-testid={`sensemaker-nav-${item.section}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onNavActivate(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onNavActivate(item)
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                height: '44px',
                px: '16px',
                borderRadius: '10px',
                cursor: 'pointer',
                bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                color: isActive ? NAV_CYAN : '#ffffff',
                transition: 'background-color 150ms ease, color 150ms ease',
                outline: 'none',
                '&:hover': {
                  bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)',
                },
              }}
            >
              {item.icon}
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '-0.1504px',
                  color: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          )
        })}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          px: '16px',
          pt: '17px',
          pb: '16px',
          flexShrink: 0,
        }}
      >
        <Box
          id="sensemaker-nav-profile-trigger"
          role="button"
          tabIndex={0}
          data-testid="sensemaker-ftux-profile"
          aria-haspopup="true"
          aria-expanded={profileMenuOpen ? 'true' : 'false'}
          aria-controls={profileMenuOpen ? 'sensemaker-nav-profile-menu' : undefined}
          onClick={onProfileMenuOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onProfileMenuOpen(e)
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            height: '60px',
            pl: '12px',
            pr: '15px',
            borderRadius: '10px',
            cursor: 'pointer',
            outline: 'none',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            '&:focus-visible': { outline: `2px solid ${NAV_CYAN}`, outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              bgcolor: AVATAR_BG,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#ffffff',
                letterSpacing: '-0.1504px',
              }}
            >
              {initials}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#ffffff',
                letterSpacing: '-0.1504px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
                color: 'rgba(255,255,255,0.6)',
                whiteSpace: 'nowrap',
              }}
            >
              {roleLabel}
            </Typography>
          </Box>

          <KeyboardArrowDownIcon
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: 24,
              flexShrink: 0,
              transform: profileMenuOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms ease',
            }}
          />
        </Box>

        <Menu
          id="sensemaker-nav-profile-menu"
          data-testid="sensemaker-nav-profile-menu"
          anchorEl={profileMenuAnchor}
          open={profileMenuOpen}
          onClose={onProfileMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 220,
                borderRadius: '10px',
                mt: '-8px',
                py: 0.5,
                boxShadow: '0px 4px 16px rgba(15, 23, 42, 0.12)',
              },
            },
          }}
        >
          <MenuItem
            data-testid="sensemaker-nav-account-settings"
            onClick={onAccountSettings}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              borderRadius: '10px',
              py: 1,
              '&:hover': { bgcolor: MENU_HOVER_BG },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                width: PROFILE_MENU_ICON_PX,
                flexShrink: 0,
                justifyContent: 'center',
              }}
            >
              <ProfileMenuIconSettings />
            </ListItemIcon>
            <ListItemText
              primary="Account Settings"
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
                color: '#0f172a',
              }}
            />
          </MenuItem>
          <MenuItem
            data-testid="sensemaker-nav-logout"
            onClick={onLogout}
            disabled={logoutPending}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              borderRadius: '10px',
              py: 1,
              '&:hover': { bgcolor: MENU_HOVER_BG },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                width: PROFILE_MENU_ICON_PX,
                flexShrink: 0,
                justifyContent: 'center',
              }}
            >
              <ProfileMenuIconLogout />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
                color: '#0f172a',
              }}
            />
          </MenuItem>
        </Menu>
      </Box>
    </>
  )
}
